import { Center, Spinner, Text } from '@chakra-ui/react'

import { WarningTwoIcon } from '@chakra-ui/icons'
import { ScreenContext, ThemeContext, useApp, useAuth, useLoadingTimeout } from '@hb/react'
import { ProfileVersion, UserProfileProps, version } from '@hb/shared'
import React, { ReactNode, useCallback, useContext, useEffect, useMemo } from 'react'
import { MissingFormsProvider } from '../../../contexts/MissingFormsContext'
import { useResolveParams } from '../../../contexts/profile'
import {
  ProfileContext,
  UseProfileArgs,
  useProfileData,
} from '../../../contexts/user/ProfileContext'
import { useNavigate } from '../../../hooks/navigation'
import { DefaultModal } from '../../Modals/DefaultModal'
import { ProfileBody as ProfileBodyV1 } from './Body/V1/ProfileBody'
import { ProfileBody as ProfileBodyV2 } from './Body/V2/ProfileBody'
import { MissingFormsModal } from './ConsentForms/MissingForms'
import { PaymentsDueModal } from './Payments/PaymentsDue/PaymentsDueModal'
import { PaymentsProvider } from './Payments/PaymentsProvider'
import { UrgentAlertsPopUp } from './UrgentAlertsPopUp'

// const profileVersion: ProfileVersion = 'v1' as ProfileVersion
export const PatientProfile: React.FC<UserProfileProps> = ({
  basePath,
  // tab,
  // subTab,
  inList,
  // onTabSelect,
  // onClose,
}) => {
  const { height: screenHeight, isMobile, width: screenWidth } = useContext(ScreenContext)
  const { appName } = useApp()
  const profileVersion: ProfileVersion = useMemo(
    () => (appName === 'providers-app' && version === 'dev' ? 'v2' : 'v1'),
    [appName],
  )
  const me = useAuth(s => s.authUser)
  const navigate = useNavigate()

  const resolvedParams = useResolveParams(profileVersion)

  const useProfileArgs = useMemo<UseProfileArgs>(
    () => ({
      profileVersion,
      basePath,
      resolvedParams,
      navigate,
    }),
    [profileVersion, basePath, resolvedParams, navigate],
  )
  const userData = useProfileData(useProfileArgs)

  const height = useMemo(() => screenHeight - (isMobile ? 10 : 20), [screenHeight, isMobile])
  const width = useMemo(() => screenWidth - (isMobile ? 10 : 20), [screenWidth, isMobile])

  const { user, selectedAssessment, loading, baseItem, baseItemType, userId } = userData
  const { loading: assessmentLoading, id: assessmentId, assessmentData } = selectedAssessment ?? {}

  const { reverseName } = user ?? {}
  useEffect(() => {
    if (reverseName) document.title = `${reverseName} - HB`
    return () => {
      document.title = 'Hamilton Billing'
    }
  }, [reverseName])

  const onClose = useCallback(() => {
    if (inList && baseItemType)
      navigate(basePath && basePath !== '/' ? `${basePath}/${baseItemType}` : `/${baseItemType}`)
    else navigate(basePath || '/')
  }, [baseItemType, basePath, inList, navigate])

  const loadingTimeout = useLoadingTimeout(loading || assessmentLoading, 200)
  const loadingText = useMemo(() => {
    if (assessmentLoading) return 'Loading assessment...'
    if (loading) return 'Loading user data...'
    return 'Loading...'
  }, [assessmentLoading, loading])

  const errorText = useMemo(() => {
    if (loadingTimeout) return null
    if (!loading && !user && userId) return `Patient with id ${userId} not found`
    if (!assessmentLoading && assessmentId && !assessmentData)
      return `Pregnancy with id ${assessmentId} not found`
    return null
  }, [user, loading, assessmentLoading, assessmentId, assessmentData, userId, loadingTimeout])

  const ProfileBody = profileVersion === 'v2' ? ProfileBodyV2 : ProfileBodyV1

  let body: ReactNode | null = null
  if (user && !loading && me) {
    body = <ProfileBody height={height} width={width} />
  } else if (loadingTimeout) {
    body = (
      <Center color="gray.600" px={2} py={4} w="100%" h="100%">
        <Spinner mr={2} />
        <Text fontSize="lg">{loadingText}</Text>
      </Center>
    )
  } else if (errorText) {
    body = (
      <Center color="red.600" px={2} py={4} w="100%" h="100%">
        <WarningTwoIcon w={5} h={5} mr={2} />
        <Text fontSize="xl">{errorText ?? 'Error loading profile'}</Text>
      </Center>
    )
  } else {
    body = null
  }

  return (
    <ProfileContext.Provider value={userData}>
      <MissingFormsProvider>
        <ThemeContext.Provider value={{ hideLabels: true, placeholderAbove: true }}>
          <PaymentsProvider>
            <DefaultModal
              isOpen={!!baseItem}
              isCentered
              closeOnOverlayClick={false}
              overlayHeader
              render={() => (
                <>
                  {body}
                  <PaymentsDueModal />
                  {assessmentLoading || loading ? null : <UrgentAlertsPopUp />}
                </>
              )}
              size="6xl"
              onClose={onClose}
              contentProps={{
                // m: '50px',
                padding: 0,
                maxH: height,
                height,
                maxWidth: '100%',
                width,
                bg: '#efefef',
                overflow: 'hidden',
              }}
              bodyProps={{
                height,
                width,
                padding: 0,
                maxHeight: height,
                overflow: 'hidden',
                display: 'flex',
                flexFlow: 'column',
                overflowY: isMobile && profileVersion === 'v1' ? 'auto' : 'hidden',
              }}
            />
          </PaymentsProvider>
        </ThemeContext.Provider>
        <MissingFormsModal />
      </MissingFormsProvider>
    </ProfileContext.Provider>
  )
}
